import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Centered status screen used in the panel during async waits.
 * Three states:
 *  - loading: spinner + message
 *  - error: error icon + message + optional retry
 */
@customElement('bia-loading-screen')
export class BiaLoadingScreen extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      min-height: 0;
      background: var(--bia-background, #fafaf7);
    }
    .wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 18px;
      padding: 32px 28px;
      text-align: center;
      animation: fade-in 280ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    @keyframes fade-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .dots {
      display: inline-flex;
      gap: 6px;
    }
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--bia-text, #0f0f0e);
      opacity: 0.25;
      animation: dot 1.2s ease-in-out infinite both;
    }
    .dot:nth-child(2) { animation-delay: 0.15s; }
    .dot:nth-child(3) { animation-delay: 0.3s; }
    @keyframes dot {
      0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
      40%           { opacity: 0.95; transform: translateY(-4px); }
    }
    .msg {
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-weight: 500;
      font-size: 16px;
      letter-spacing: -0.01em;
      color: var(--bia-text, #0f0f0e);
      max-width: 260px;
      line-height: 1.4;
    }
    .err-mark {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--bia-danger-bg, #fef2f2);
      color: var(--bia-danger, #b91c1c);
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .err-mark svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .err-msg {
      font-family: var(--bia-font-family, sans-serif);
      font-size: 13px;
      line-height: 1.5;
      color: var(--bia-text, #0f0f0e);
      max-width: 280px;
    }
    button.retry {
      margin-top: 4px;
      padding: 9px 20px;
      border-radius: 999px;
      border: 1px solid var(--bia-text, #0f0f0e);
      background: transparent;
      color: var(--bia-text, #0f0f0e);
      font-family: var(--bia-font-family, sans-serif);
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.02em;
      cursor: pointer;
      transition: background 140ms ease, color 140ms ease;
    }
    button.retry:hover {
      background: var(--bia-text, #0f0f0e);
      color: var(--bia-primary-text, #fafaf7);
    }
  `;

  @property({ type: String }) message = 'Carregando';
  @property({ type: String }) error = '';
  @property({ type: Boolean }) retryable = false;

  private onRetry(): void {
    this.dispatchEvent(new CustomEvent('retry', { bubbles: true, composed: true }));
  }

  render() {
    if (this.error) {
      return html`
        <div class="wrapper">
          <span class="err-mark">
            <svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/></svg>
          </span>
          <div class="err-msg">${this.error}</div>
          ${this.retryable
            ? html`<button class="retry" @click=${this.onRetry}>Tentar novamente</button>`
            : null}
        </div>
      `;
    }
    return html`
      <div class="wrapper">
        <div class="dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <div class="msg">${this.message}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bia-loading-screen': BiaLoadingScreen;
  }
}
